import {
    Alert,
    Button,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import { PinSchema, type Player } from '../schemas/auth.schema';
import { colors, MIN_TAP_TARGET } from '../themes/theme';

interface PlayerPickerProps {
    players: Player[];
    pending: boolean;
    error: string | null;
    onSubmit: (userId: number, pin: string) => void;
    onBack?: () => void;
}

/** Tap who you are, then confirm with your PIN. */
export function PlayerPicker({ players, pending, error, onSubmit, onBack }: PlayerPickerProps) {
    const [userId, setUserId] = useState<number | null>(null);
    const [pin, setPin] = useState('');
    const pinCheck = PinSchema.safeParse(pin);

    return (
        <Stack
            component="form"
            spacing={2}
            onSubmit={(event) => {
                event.preventDefault();
                if (userId === null || !pinCheck.success) return;
                onSubmit(userId, pin);
            }}
        >
            <Typography variant="overline" sx={{ color: colors.muted }}>
                Who are you
            </Typography>

            <ToggleButtonGroup
                value={userId}
                exclusive
                onChange={(_event, value: number | null) => setUserId(value)}
                sx={{ flexWrap: 'wrap', gap: 1, '& .MuiToggleButton-root': { borderRadius: '3px !important', border: `1px solid ${colors.line} !important` } }}
            >
                {players.map((player) => (
                    <ToggleButton
                        key={player.id}
                        value={player.id}
                        sx={{ minHeight: MIN_TAP_TARGET, px: 2, textTransform: 'none' }}
                    >
                        {player.handle}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>

            <TextField
                label="Your PIN"
                value={pin}
                onChange={(event) => setPin(event.target.value.replace(/\D/g, ''))}
                type="tel"
                inputMode="numeric"
                autoComplete="current-password"
                disabled={userId === null}
                helperText={userId === null ? 'Pick your name first' : ' '}
            />

            {error && <Alert severity="error">{error}</Alert>}

            <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={userId === null || !pinCheck.success || pending}
            >
                {pending ? 'Signing in…' : 'Sign in'}
            </Button>

            {onBack && (
                <Button color="inherit" onClick={onBack}>
                    Back
                </Button>
            )}
        </Stack>
    );
}
